import { Columns, ListFilter, Rows, X } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { TextFilter } from './filters/text'
import { SelectFilter } from './filters/select'
import { DateRangeFilter } from './filters/date-range'
import type { DateTimeRange } from './filters/date-range'
import type { Density, FilterConfig } from './types'
import type { Table } from '@tanstack/react-table'
import { Separator } from '@/components/ui/separator'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

type ToolbarProps<TData> = {
  table: Table<TData>
  globalSearch?: boolean | { placeholder?: string }
  globalQuery: string | undefined
  onGlobalQueryChange: (value: string | undefined) => void
  filters?: Array<FilterConfig>
  filterValues: Record<string, unknown>
  onFilterChange: (id: string, value: unknown) => void
  onClearAllFilters?: () => void
  enableColumnVisibility: boolean
  density: Density
  showDensityToggle: boolean
  onDensityChange: (d: Density) => void
  isSearchPending?: boolean
}

export function Toolbar<TData>({
  table,
  globalSearch,
  globalQuery,
  onGlobalQueryChange,
  filters,
  filterValues,
  onFilterChange,
  onClearAllFilters,
  enableColumnVisibility,
  density,
  showDensityToggle,
  onDensityChange,
  isSearchPending = false,
}: ToolbarProps<TData>) {
  const { t: tUi } = useTranslation('ui')
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Clear search on Escape key
    if (e.key === 'Escape' && globalQuery) {
      e.preventDefault()
      onGlobalQueryChange(undefined)
    }
  }

  // Count active filters (non-empty values)
  const activeFilterCount = Object.values(filterValues).filter((v) => {
    if (v === undefined || v === null || v === '') return false
    if (typeof v === 'string' && v === 'all') return false // 'all' is typically the default
    if (typeof v === 'object') {
      // Check for empty date range
      const range = v as { from?: unknown; to?: unknown }
      return range.from !== undefined || range.to !== undefined
    }
    return true
  }).length

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2 items-center">
        {globalSearch && (
          <div className="relative">
            <Input
              value={globalQuery ?? ''}
              onChange={(e) => onGlobalQueryChange(e.target.value || undefined)}
              onKeyDown={handleSearchKeyDown}
              placeholder={
                typeof globalSearch === 'object' && globalSearch.placeholder
                  ? globalSearch.placeholder
                  : 'Search...'
              }
              className="w-[260px] pr-16"
            />
            {isSearchPending && (
              <div className="absolute right-8 top-1/2 -translate-y-1/2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
              </div>
            )}
            <button
              type="button"
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => onGlobalQueryChange(undefined)}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {filters && filters.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
              <ListFilter className="w-4 h-4 text-muted-foreground" />
              {activeFilterCount > 0 && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
                  {activeFilterCount}
                </span>
              )}
            </div>
            {filters.map((f) => {
              const value = filterValues[f.id]
              if (f.type === 'text') {
                return (
                  <TextFilter
                    key={f.id}
                    value={(value as string | undefined) ?? undefined}
                    placeholder={f.placeholder ?? f.label}
                    onChange={(v) => onFilterChange(f.id, v)}
                  />
                )
              }
              if (f.type === 'select' && f.options) {
                return (
                  <SelectFilter
                    key={f.id}
                    value={(value as string | undefined) ?? undefined}
                    placeholder={f.placeholder ?? f.label}
                    options={f.options}
                    onChange={(v) => onFilterChange(f.id, v)}
                  />
                )
              }
              if (f.type === 'date-range') {
                return (
                  <DateRangeFilter
                    key={f.id}
                    value={value as DateTimeRange | undefined}
                    placeholder={f.placeholder ?? f.label}
                    onChange={(v) => onFilterChange(f.id, v)}
                    maxDate={f.maxDate}
                    minDate={f.minDate}
                    enableTime={f.enableTime}
                  />
                )
              }
              if (f.type === 'boolean') {
                const active = value === true
                return (
                  <Button
                    key={f.id}
                    variant={active ? 'default' : 'outline'}
                    size="sm"
                    className="h-8"
                    onClick={() =>
                      onFilterChange(f.id, active ? undefined : true)
                    }
                  >
                    {f.label}
                  </Button>
                )
              }
              return null
            })}
            {activeFilterCount > 0 && onClearAllFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearAllFilters}
                className="h-8 px-2 text-muted-foreground hover:text-foreground"
              >
                {tUi('toolbar.clearAll')}
                <X className="ml-1 h-3 w-3" />
              </Button>
            )}
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          {enableColumnVisibility && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Columns className="w-4 h-4 mr-2" /> {tUi('toolbar.columns')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table.getAllLeafColumns().map((col) => {
                  const id = col.id
                  const meta = (col.columnDef as any).meta
                  const label =
                    typeof col.columnDef.header === 'string'
                      ? col.columnDef.header
                      : (meta?.label ?? id)
                  return (
                    <DropdownMenuCheckboxItem
                      key={id}
                      className="capitalize"
                      checked={col.getIsVisible()}
                      onCheckedChange={(v) => col.toggleVisibility(!!v)}
                    >
                      {label}
                    </DropdownMenuCheckboxItem>
                  )
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {showDensityToggle && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Rows className="w-4 h-4 mr-2" /> {tUi('toolbar.density')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {(['compact', 'normal', 'comfortable'] as Array<Density>).map(
                  (d) => (
                    <DropdownMenuCheckboxItem
                      key={d}
                      checked={d === density}
                      onCheckedChange={() => onDensityChange(d)}
                    >
                      {tUi(`density.${d}` as const)}
                    </DropdownMenuCheckboxItem>
                  ),
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <Separator />
    </div>
  )
}
